import axios, { retryOnErrors } from '@fs/zion-axios'
import CacheService from '@fs/zion-cache-service'
import { i18n } from '@fs/zion-locale'
import { isValidPid, MAX_ORD_READY_COUNT, ORD_READY_TIMEOUT } from './helpers'
import { mungeTreeData, personDemunge, personMunge } from './mungers'
import {
  tryDemoCreatePerson,
  tryDemoEditPerson,
  tryDemoGetPersonsPortraits,
  tryDemoGetReservedNames,
  tryDemoGetTempleForId,
  tryDemoGetTreeData,
  tryDemoPollOrdinancesReadyNames,
  tryDemoRequestOrdinancesReadyNames,
  tryDemoReserveName,
  tryDemoSetWorkingPersonData,
  tryDemoShareWithIndividual,
  tryDemoShareWithTemple,
  tryDemoUnshareWithTemple,
} from './tryDemoServices'

const config = {
  retry: 2,
  noResponseRetries: 5,
  statusCodesToRetry: [[100, 199], [206], [409, 429], [500, 599]],
  retryDelay: 150,
}
retryOnErrors(axios, config)

const serviceBaseUrl = '/service/temple/guide-me'
const matchUrl = `${serviceBaseUrl}/match`
const memberDataUrl = `${serviceBaseUrl}/member`
const memberLookupUrl = `${serviceBaseUrl}/member/account-lookup`
const ordinancesReadyUrl = `${serviceBaseUrl}/ordinances-ready`
const ordinanceStatusUrl = `${serviceBaseUrl}/ordinance-status`
const portraitsUrl = `${serviceBaseUrl}/portraits`
const printCardsUrl = `${serviceBaseUrl}/cards`
const reservationsUrl = `${serviceBaseUrl}/reservations`
const templeUrl = `${serviceBaseUrl}/temple-info`
const treePersonUrl = `${serviceBaseUrl}/tree/person`
const unitUrl = `${serviceBaseUrl}/unit`
const userAccountUrl = `${serviceBaseUrl}/user-account`
const workingPersonUrl = `${serviceBaseUrl}/person`

const cache = { storeName: 'ftte', type: 'session' }
const cacheService = new CacheService({ cache, strategy: 'cache-and-network' })

// /////////////////////
// User Account Calls //
// /////////////////////
// #region authentication calls
export function getFsAccountActivationStatus(helpeeUuid) {
  // If there is no helpeeUuid, then they're going through the experience for their own account -- It's guaranteed that the user has a FamilySearch account
  if (!helpeeUuid) return Promise.resolve({ cisId: true, fullyRegistered: true, sproutStatus: true })
  return axios.get(`${userAccountUrl}?helpeeUuid=${helpeeUuid}`).then((res) => res.data)
}

export function getSproutStatus(helpeeUuid) {
  if (!helpeeUuid) return Promise.reject(new Error('getSproutStatus(): Missing helpeeUuid parameter.'))
  return axios.get(`${userAccountUrl}?helpeeUuid=${helpeeUuid}`).then((res) => res.data.sproutStatus)
}

export function initiateFsAccount(cmisUuid, preferredLanguage) {
  if (!cmisUuid) return Promise.reject(new Error('initiateFsAccount(): Missing uuid object.'))
  return axios.post(`${userAccountUrl}?helpeeUuid=${cmisUuid}`, {
    cmisUuid,
    preferredLanguage,
  })
}

export function transferAccount(token, userData) {
  if (!token) return Promise.reject(new Error('Look up token is required.'))
  const url = `${userAccountUrl}`
  return axios.put(url, {
    token,
    sexCode: userData?.sexCode,
    birthDate: userData?.birth?.date,
  })
}

export function setShowTempleOptionsSetting(showTempleOptions) {
  // Does that which was done on other worlds: https://github.com/fs-webdev/settings-react/blob/master/src/hooks/useShowTempleInfo.js
  return axios.post('/service/tree/tree-data/profile/pref/showLDSTempleInfo', { pref: !!showTempleOptions })
}
// #endregion

// ////////////////////
// TreeList Calls //
// ////////////////////
// #region treeList calls
export function getTreeData(helpeeUuid, relation = 'PARENTS', isTryDemo = false) {
  if (isTryDemo) {
    return tryDemoGetTreeData(`${treePersonUrl}?lang=${i18n.language}`)
  }
  let url = `${treePersonUrl}?lang=${i18n.language}&relationships=${relation.toUpperCase()}`
  // Since CHILDREN alone doesn't give us the spouses person info, we need to attach SPOUSES to the call as well
  if (relation === 'CHILDREN') url += ',SPOUSES'
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`
  return axios.get(url).then(mungeTreeData)
}

// #endregion

// ////////////////////
// Member Data Calls //
// ////////////////////
// #region member data calls
// If no uuid is provided then it gets the member data for the user that is signed in
export function getMemberData(helpeeUuid) {
  let url = `${memberDataUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  return axios.get(url)
}

export function getMembershipToken(helpeeUuid) {
  if (!helpeeUuid) return Promise.reject(new Error('helpeeUuid required to get membership token.'))
  const tokenRegex = /(?<=account-lookup\/).*/
  const url = `${memberLookupUrl}?helpeeUuid=${helpeeUuid}`
  return axios.post(url).then((response) => tokenRegex.exec(response.headers.location)?.[0])
}

export function lookupMembershipData(token) {
  if (!token) return Promise.reject(new Error('Look up token is required.'))
  const url = `${memberLookupUrl}/${token}`
  return axios.get(url)
}
// #endregion

// ///////////////////////////
// WorkingPerson CRUD calls //
// ///////////////////////////
// #region WorkingPerson CRUD calls
export function getWorkingPersonContainer(helpeeUuid) {
  let url = `${workingPersonUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  return axios.get(url, { headers: { Accept: 'application/vnd.familysearch.ftte.v1+json' } }).then((res) => {
    // MUNGE TIME!
    // Only munge Birth and Death Dates and Places to match the (non-)standardized shape our components expect.
    // Keep everything else the same shape as the backend provides us.
    const workingPersonContainer = res?.data
    if (!workingPersonContainer) {
      return {
        metadata: null,
        persons: [],
        coupleRelationships: [],
        parentChildRelationships: [],
      }
    }

    // Munge person
    workingPersonContainer.persons.forEach((person, index, array) => {
      array[index] = personMunge(person)
    })

    return workingPersonContainer
  })
}

export function setWorkingPersonData(
  helpeeUuid,
  metadata,
  persons = [],
  coupleRelationships = [],
  parentChildRelationships = [],
  isTryDemo = false
) {
  if (isTryDemo) {
    return tryDemoSetWorkingPersonData(metadata, persons, coupleRelationships, parentChildRelationships)
  }
  const workingPersonContainer = {
    metadata,
    persons: structuredClone(persons),
    coupleRelationships: structuredClone(coupleRelationships),
    parentChildRelationships: structuredClone(parentChildRelationships),
  }
  if (persons[0]?.id && !isValidPid(persons[0]?.id)) {
    workingPersonContainer.matchId = persons[0]?.id
  }

  workingPersonContainer.persons?.forEach((person, index, array) => {
    array[index] = personDemunge(person)
    delete array[index].baptism
    delete array[index].confirmation
  })

  // Update backend state
  let url = `${workingPersonUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  return axios.put(url, workingPersonContainer, {
    headers: { 'Content-Type': 'application/vnd.familysearch.ftte.v1+json' },
  })
}

export function deleteWorkingPersonData(helpeeUuid) {
  let url = `${workingPersonUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  return axios.delete(url)
}
// #endregion

// /////////////////////////
// TreePerson CRUD calls //
// /////////////////////////
// #region treePerson CRUD calls
export function createPerson(helpeeUuid, metadata, persons, isTryDemo, useMockMatchData) {
  if (useMockMatchData) return Promise.resolve('')
  const workingPerson = persons?.find((person) => person.matchPurpose === 'TARGET')
  if (metadata?.connectedRelatives && workingPerson.id in metadata.connectedRelatives) return Promise.resolve('')
  if (isTryDemo) {
    return tryDemoCreatePerson(metadata, persons)
  }
  let url = `${treePersonUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  return axios.post(url).then((res) => res.data ?? '')
}

export function editPerson(helpeeUuid, person, parentChildRelationships, isTryDemo) {
  if (!person) return Promise.reject(new Error('editPerson(): Missing person.'))
  if (!Object.keys(person).length) return Promise.reject(new Error('editPerson(): Empty person object.'))
  if (!person?.id) return Promise.reject(new Error('editPerson(): Missing id in person object.'))
  if (isTryDemo) {
    return tryDemoEditPerson(person)
  }

  let url = `${treePersonUrl}/${person.id}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  const parentChildRelationshipTypeNeedsUpdate =
    parentChildRelationships[0]?.newParentTypes1 || parentChildRelationships[0]?.newParentTypes2
  const modifyTreePersonRequest = {
    workingPerson: person,
    parentChildRelationshipUpdate:
      parentChildRelationships[0] && parentChildRelationshipTypeNeedsUpdate && parentChildRelationships[0],
  }
  return axios.put(url, modifyTreePersonRequest, {
    headers: {
      'Content-Type': 'application/x-ftte-v2+json',
    },
  })
}

export function getRelationToPerson(memberUuid, personId, isTryDemo) {
  if (!personId) return Promise.reject(new Error('getRelationToPerson(): Missing personId parameter.'))
  if (isTryDemo) return Promise.resolve({})
  let url = `${treePersonUrl}/${personId}/relationship?lang=${i18n.language}`
  if (memberUuid) url += `&helpeeUuid=${memberUuid}`
  return axios.get(url)
}

// Used by other service calls -- only exported for testing
export function getPersonsPortraits(memberUuid, pids, isTryDemo) {
  if (!pids) return Promise.reject(new Error('getPersonsPortraits(): Missing pids parameter.'))
  if (!pids.length) return Promise.resolve({})
  if (isTryDemo) {
    return tryDemoGetPersonsPortraits()
  }
  let url = `${portraitsUrl}?personIds=${pids.join(',')}`
  if (memberUuid) url += `&helpeeUuid=${memberUuid}`
  return axios.get(url).then((res) => res.data)
}
// #endregion

// /////////////////////////
// Ordinance Status calls //
// /////////////////////////
// #region ordinance-status calls
export function getOrdinanceStatus(helpeeUuid, pid, ordinances = 'BAPTISM,CONFIRMATION') {
  if (!pid) return Promise.reject(new Error('getOrdinanceStatus(): Missing person id.'))

  let url = `${ordinanceStatusUrl}/${pid}?ordinances=${ordinances}`
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`
  return axios.get(url)
}
// #endregion

// ///////////////////////////
// Temple Reservation calls //
// ///////////////////////////
// #region Temple Reservation calls
export function getReservedNames(helpeeUuid, isTryDemo) {
  if (isTryDemo) return tryDemoGetReservedNames()

  let url = `${reservationsUrl}?lang=${i18n.language}`
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`
  const [serverPromise, cachePromise] = cacheService.get(url, `${helpeeUuid}-reservations`)
  return { cachePromise, serverPromise }
}

export function getTempleForId(helpeeUuid, reservations, forQr = false, isTryDemo = false) {
  if (!reservations) return Promise.reject(new Error('getTempleForId(): Missing reservations.'))
  if (!reservations.length) {
    return Promise.reject(new Error('getTempleForId(): Reservations can not be an empty array.'))
  }
  if (isTryDemo) {
    return tryDemoGetTempleForId()
  }

  // `value` must be a string for the QR code to work.
  let url = `${printCardsUrl}?lang=${i18n.language}&forQr=${forQr}`
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`
  return axios
    .post(url, reservations, {
      headers: {
        Accept: 'application/json',
      },
    })
    .then((res) => res?.data?.forId)
}

export function getTempleInfo(helpeeUuid, locale = 'en') {
  const preCacheHook = (data) => {
    data.locale = locale
    return data
  }
  let url = `${templeUrl}?lang=${i18n.language}`
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`
  const [serverPromise, cachePromise] = cacheService.get(url, `${helpeeUuid}-temple-info`, { preCacheHook })
  return { serverPromise, cachePromise }
}

export async function getTempleNameCardsPdfId(helpeeUuid, forId) {
  if (!forId) {
    return Promise.reject(new Error('getTempleNameCardsPdfId(): Missing forId.'))
  }

  const timeZoneName = encodeURI(Intl.DateTimeFormat().resolvedOptions().timeZone)

  let url = `${printCardsUrl}/${forId}?zoneId=${timeZoneName}&lang=${i18n.language}`
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`

  const res = await axios.post(url, null, {
    timeout: 20000,
    headers: {
      Accept: 'application/json',
      'Accept-Language': i18n.language,
      'Content-Type': 'application/json',
    },
  })

  return res?.data?.pdfId
}

export function reserveName(helpeeUuid, workingPerson, metadata, isTryDemo, useMockMatchData) {
  if (useMockMatchData) return Promise.resolve()
  if (!workingPerson?.id) return Promise.reject(new Error('reserveName(): Missing pid.'))
  if (isTryDemo) {
    return tryDemoReserveName(workingPerson, metadata)
  }
  let url = `${reservationsUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  const ordinances = ['BAPTISM', 'CONFIRMATION'].filter((type) => {
    const ordinanceStatus = workingPerson[type.toLowerCase()]?.status
    return ordinanceStatus !== 'COMPLETED'
  })

  return axios.post(url, [{ personId: workingPerson.id, reservationInventoryType: 'PERSONAL', ordinances }])
}

export function shareWithIndividual(helpeeUuid, pids, senderName, receiverName, receiverEmail, isTryDemo) {
  if (!pids) return Promise.reject(new Error('shareWithIndividual(): Missing pids.'))
  if (!pids.length) return Promise.reject(new Error('shareWithIndividual(): Empty pids array.'))
  if (!senderName) return Promise.reject(new Error('shareWithIndividual(): Missing senderName.'))
  if (!receiverName) return Promise.reject(new Error('shareWithIndividual(): Missing receiverName.'))
  if (!receiverEmail) return Promise.reject(new Error('shareWithIndividual(): Missing receiverEmail.'))
  if (isTryDemo) {
    return tryDemoShareWithIndividual(pids)
  }
  let url = `${reservationsUrl}/transfer`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  const body = { persons: pids, receiverEmail, receiverName, senderName }
  return axios.post(url, body)
}

export function shareWithTemple(helpeeUuid, pids, isTryDemo) {
  if (!pids) return Promise.reject(new Error('shareWithTemple(): Missing pids.'))
  if (isTryDemo) {
    return tryDemoShareWithTemple(pids)
  }

  let url = `${reservationsUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}&type=share`
  const body = pids.map((pid) => ({
    personId: pid,
    reservationInventoryType: 'CHURCH',
    ordinances: ['BAPTISM', 'CONFIRMATION'],
  }))
  return axios.post(url, body)
}

export function unshareWithTemple(helpeeUuid, pids, isTryDemo) {
  if (!pids) return Promise.reject(new Error('unshareWithTemple(): Missing pids.'))
  if (isTryDemo) {
    return tryDemoUnshareWithTemple(pids)
  }
  let url = `${reservationsUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}&type=unshare`
  const body = pids.map((pid) => ({
    personId: pid,
    reservationInventoryType: 'PERSONAL',
    ordinances: ['BAPTISM', 'CONFIRMATION'],
  }))
  return axios.post(url, body)
}
// #endregion

// /////////////////////////
// Ordinances Ready calls //
// /////////////////////////
// #region Ordinances Ready calls
export function requestOrdinancesReadyNames(helpeeUuid, sex, isTryDemo, lang = 'en') {
  if (!sex) return Promise.reject(new Error('requestOrdinancesReadyNames(): Missing sex.'))
  if (isTryDemo) {
    return tryDemoRequestOrdinancesReadyNames()
  }
  const ordinanceRequests = [
    {
      quantity: MAX_ORD_READY_COUNT,
      ordinanceType: 'BAPTISM',
    },
    {
      quantity: MAX_ORD_READY_COUNT,
      ordinanceType: 'CONFIRMATION',
    },
  ]

  let url = `${ordinancesReadyUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  return axios
    .post(url, { lang, sex, ordinanceRequests }, { timeout: ORD_READY_TIMEOUT })
    .then((res) => res.headers.location.split('/').at(-1)) // Return token
}

export const ORD_READY_POOL_EMPTY_ERROR_MSG =
  'Ordinances ready request is still unfulfilled after checking in all buckets'

export function pollOrdinancesReadyNames(helpeeUuid, token, memberPersonSex, personsOnReservationList, t, isTryDemo) {
  if (!token) return Promise.reject(new Error('pollOrdinancesReadyNames(): Missing token.'))
  if (isTryDemo) {
    return tryDemoPollOrdinancesReadyNames(memberPersonSex, personsOnReservationList, t)
  }
  let url = `${ordinancesReadyUrl}/${token}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`

  return new Promise((resolve, reject) => {
    const POLL_INTERVAL_MS = 1000
    const MAX_POLLING_TIME_MS = 20000
    async function checkStatus(pollCallsCount) {
      const { status, data } = await axios.get(url, { timeout: ORD_READY_TIMEOUT })
      switch (status) {
        case 200:
        case 206:
          resolve(data)
          break
        case 204:
          // We've seen this happen in integration because the pool of names was empty
          reject(new Error(ORD_READY_POOL_EMPTY_ERROR_MSG))
          break
        case 202:
          if (pollCallsCount * POLL_INTERVAL_MS >= MAX_POLLING_TIME_MS) {
            reject(new Error('We apparently never could get any names from ordinances ready in a timely manner'))
          } else setTimeout(() => checkStatus(pollCallsCount + 1), POLL_INTERVAL_MS)
          break
        case 401:
        case 404:
        case 500:
        default:
          reject()
      }
    }
    checkStatus(0)
  })
}
// #endregion

// //////////////
// Match calls //
// //////////////
// #region Ordinances Ready calls
export function getPotentialMatches(helpeeUuid, minConfidence, useMockMatchData, workingPerson, isTryDemo) {
  if (useMockMatchData) return Promise.resolve({ data: generateMockMatchData(workingPerson), status: 200 })
  if (isTryDemo) return Promise.resolve({ data: [], status: 204 })
  let url = `${matchUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  if (minConfidence) url += `${helpeeUuid ? '&' : '?'}minConfidence=${minConfidence}`
  return axios.get(url)
}

function generateMockMatchData(workingPerson) {
  const demungedWorkingPerson = personDemunge(workingPerson)
  const getBaptismStatus = (i) => {
    if (i === 1) return 'COMPLETED'
    if (i === 2) return 'COMPLETED'
    return 'READY'
  }
  const getConfirmationStatus = (i) => {
    if (i === 1) return 'COMPLETED'
    if (i === 2) return 'READY'
    return 'READY'
  }
  return [1, 2, 3].map((i) => {
    const baptismStatus = getBaptismStatus(i)
    const confirmationStatus = getConfirmationStatus(i)
    return {
      confidence: 5,
      id: `mock-match-id-${i}-${baptismStatus}-${confirmationStatus}`,
      person: {
        baptism: {
          status: baptismStatus,
        },
        confirmation: {
          status: confirmationStatus,
        },
        couplesRelationships:
          i === 1
            ? [1, 2, 3].map((coupleI) => {
                return {
                  selfId: `mock-match-id-${i}`,
                  otherId: `mock-match-spouse${coupleI}-id`,
                }
              })
            : [],
        id: `mock-match-id-${i}`,
        lifespanBegin: demungedWorkingPerson.lifespanBegin,
        lifespanEnd: demungedWorkingPerson.lifespanEnd,
        living: false,
        nameForms: demungedWorkingPerson.nameForms,
        parentChildRelationshipsAsParent:
          i === 1
            ? [1, 2, 3, 4].map((childI) => {
                return {
                  childId: `mock-match-child${childI}-id`,
                }
              })
            : [],
        parentsPairings:
          i === 1
            ? [1, 2, 3].map((parentI) => {
                const adjustedParentI = parentI * 2
                return {
                  personId1: `mock-match-parent${adjustedParentI - 1}-id`,
                  personId2: `mock-match-parent${adjustedParentI}-id`,
                }
              })
            : [],
        relatives:
          i === 1
            ? {
                'mock-match-spouse1-id': {
                  sex: demungedWorkingPerson.sex === 'MALE' ? 'FEMALE' : 'MALE',
                  nameForms: [
                    {
                      fullText: 'Spouse One',
                    },
                  ],
                },
                'mock-match-spouse2-id': {
                  sex: demungedWorkingPerson.sex === 'MALE' ? 'FEMALE' : 'MALE',
                  nameForms: [
                    {
                      fullText: 'Spouse Two',
                    },
                  ],
                },
                'mock-match-spouse3-id': {
                  sex: demungedWorkingPerson.sex === 'MALE' ? 'FEMALE' : 'MALE',
                  nameForms: [
                    {
                      fullText: 'Spouse Three',
                    },
                  ],
                },
                'mock-match-child1-id': {
                  sex: 'FEMALE',
                  nameForms: [
                    {
                      fullText: 'Child One',
                    },
                  ],
                },
                'mock-match-child2-id': {
                  sex: 'MALE',
                  nameForms: [
                    {
                      fullText: 'Child Two',
                    },
                  ],
                },
                'mock-match-child3-id': {
                  sex: 'FEMALE',
                  nameForms: [
                    {
                      fullText: 'Child Three',
                    },
                  ],
                },
                'mock-match-child4-id': {
                  sex: 'MALE',
                  nameForms: [
                    {
                      fullText: 'Child Four',
                    },
                  ],
                },
                'mock-match-parent1-id': {
                  sex: 'MALE',
                  nameForms: [
                    {
                      fullText: 'Parent One',
                    },
                  ],
                },
                'mock-match-parent2-id': {
                  sex: 'FEMALE',
                  nameForms: [
                    {
                      fullText: 'Parent Two',
                    },
                  ],
                },
                'mock-match-parent3-id': {
                  sex: 'MALE',
                  nameForms: [
                    {
                      fullText: 'Parent Three',
                    },
                  ],
                },
                'mock-match-parent4-id': {
                  sex: 'FEMALE',
                  nameForms: [
                    {
                      fullText: 'Parent Four',
                    },
                  ],
                },
                'mock-match-parent5-id': {
                  sex: 'MALE',
                  nameForms: [
                    {
                      fullText: 'Parent Five',
                    },
                  ],
                },
                'mock-match-parent6-id': {
                  sex: 'FEMALE',
                  nameForms: [
                    {
                      fullText: 'Parent Six',
                    },
                  ],
                },
              }
            : undefined,
        sex: demungedWorkingPerson.sex,
      },
    }
  })
}

export function getPotentialMatchesWithWorkingPerson(helpeeUuid, minConfidence) {
  let url = `${matchUrl}`
  if (helpeeUuid) url += `?helpeeUuid=${helpeeUuid}`
  if (minConfidence) url += `${helpeeUuid ? '&' : '?'}minConfidence=${minConfidence}`
  return axios.post(url, {}).then((res) => res?.data)
}
// #endregion

// /////////////
// Unit calls //
// /////////////
// #region ordinance-status calls
export function getUnitDelegates(helpeeUuid) {
  let url = `${unitUrl}/delegates?lang=${i18n.language}`
  if (helpeeUuid) url += `&helpeeUuid=${helpeeUuid}`
  return axios.get(url).then((res) => res.data)
}

export function getUnitDirectory(helpeeUuid) {
  if (!helpeeUuid) return Promise.reject(new Error('getUnitDirectory(): Missing helpeeUuid.'))
  return axios.get(`${unitUrl}/directory?helpeeUuid=${helpeeUuid}`).then((res) => res.data)
}
// #endregion
